import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import { useTimer } from '../contexts/TimerContext';
import { api } from '../config/api';
import SnacksSelection from '../components/SnacksSelection';
import ProgressStepper from '../components/ProgressStepper';
import CancelTransactionPopup from '../components/CancelTransactionPopup';

const SnacksPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { movie: globalMovie, showtime: globalShowtime, theater: globalTheater, selectedSeats: globalSelectedSeats, selectedSnacks, vistaTransactionId: bookingTransactionId, setSelectedSnacks, setMovie, setShowtime, setTheater, setVistaTransactionId, setQuantity, setSelectedSeats } = useBooking();
  const { isExpired, resetTimer } = useTimer();
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [restored, setRestored] = useState(false);

  
  // Get booking data from navigation state or global state
  const movie = useMemo(
    () => location.state?.movie || globalMovie || null,
    [location.state?.movie, globalMovie]
  );
  
  
  const showtime = useMemo(
    () => location.state?.showtime || globalShowtime || null,
    [location.state?.showtime, globalShowtime]
  );
  
  
  const theater = useMemo(() => location.state?.theater || globalTheater || null, [location.state?.theater, globalTheater]);

  const selectedSeats = useMemo(() => globalSelectedSeats || location.state?.selectedSeats || [], [globalSelectedSeats, location.state?.selectedSeats]);
  
  // Initialize snacks from location state or global state
  const initialSnacks = location.state?.snacks || selectedSnacks || [];
  const [snacks, setSnacks] = useState(initialSnacks);
  
  // Booking fee settings
  const [convenienceFeeSettings, setConvenienceFeeSettings] = useState({ enabled: false, type: 'fixed', value: 0 });
  const [bookingFeeTiers, setBookingFeeTiers] = useState(null);
  // Back navigation confirmation
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancellingTransaction, setIsCancellingTransaction] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  const isNavigatingBackRef = useRef(false);

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  useEffect(() => {
    const saved = sessionStorage.getItem('seat-booking');
    if (!saved) {
      setRestored(true);
      return;
    }
  
    try {
      const parsed = JSON.parse(saved);
  
      if (parsed.movie) setMovie(parsed.movie);
      if (parsed.showtime) setShowtime(parsed.showtime);
      if (parsed.theater) setTheater(parsed.theater);
      if (parsed.selectedSeats) setSelectedSeats(parsed.selectedSeats);
      if (parsed.vistaTransactionId) setVistaTransactionId(parsed.vistaTransactionId);
      if (parsed.quantity) setQuantity(parsed.quantity);
    } catch (e) {
      console.error('Restore failed', e);
    } finally {
      setRestored(true); // ✅ THIS LINE WAS MISSING
    }
  }, []);
  
  
  // Update global booking state when component mounts
  useEffect(() => {
    if (movie) setMovie(movie);
    if (showtime) setShowtime(showtime);
    if (theater) setTheater(theater);
  }, [movie, showtime, theater, setMovie, setShowtime, setTheater]);

  // Fetch convenience fee settings and booking fee tiers on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await api.getSettings();
        if (settings && settings.convenienceFee) {
          setConvenienceFeeSettings(settings.convenienceFee);
        }
        // Set booking fee tiers if available (tiered system takes precedence)
        if (settings && settings.bookingFeeTiers && Array.isArray(settings.bookingFeeTiers) && settings.bookingFeeTiers.length > 0) {
          setBookingFeeTiers(settings.bookingFeeTiers);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Use defaults if fetch fails
      }
    };
    fetchSettings();
  }, []);

  // Sync snacks when location state or global selectedSnacks change
  useEffect(() => {
    const snacksFromState = location.state?.snacks || selectedSnacks || [];
    setSnacks(prevSnacks => {
      // Only update if the snacks are actually different
      const prevSnacksStr = JSON.stringify(prevSnacks);
      const newSnacksStr = JSON.stringify(snacksFromState);
      if (prevSnacksStr !== newSnacksStr) {
        // Also update global state if it's different and came from location state
        if (location.state?.snacks && JSON.stringify(snacksFromState) !== JSON.stringify(selectedSnacks)) {
          setSelectedSnacks(snacksFromState);
        }
        return snacksFromState;
      }
      return prevSnacks;
    });
  }, [location.state?.snacks, selectedSnacks, setSelectedSnacks]);

  // // // Check if user has selected seats, redirect if not
  // // useEffect(() => {
  // //   // Only check after a short delay to allow state to settle
  // //   const timer = setTimeout(() => {
  // //     // If no seats are selected, redirect back
  // //     if (!selectedSeats || selectedSeats.length === 0) {
  // //       // If no show is selected either, redirect to home
  // //       if (!showtime || !theater) {
  // //         showError('Please select a show and seats first!');
  // //         navigate('/');
  // //         return;
  // //       }
  // //       // If show is selected but no seats, redirect to seat selection
  // //       showError('Please select your seats first!');
  // //       navigate('/seat-selection');
  // //       return;
  // //     }
  // //   }, 100); // Small delay to allow state updates

  //   return () => clearTimeout(timer);
  // }, [selectedSeats, showtime, theater, navigate]);
  // Redirect ONLY after restore is completed
useEffect(() => {
  if (!restored) return;

  if (!selectedSeats || selectedSeats.length === 0) {
    if (!showtime || !theater) {
      navigate('/', { replace: true });
    } else {
      navigate('/seat-selection', { replace: true });
    }
  }
}, [restored, selectedSeats, showtime, theater, navigate]);


  // Handle timer expiration
  useEffect(() => {
    if (!isExpired) return;
  
    showError('Session expired! Your booking session has been cleared. Please start over.');
    resetTimer();
  
    if (!movie?.id) {
      navigate('/', { replace: true });
      return;
    }
  
    navigate(`/booking/${movie.id}`);
  }, [isExpired, resetTimer, navigate, movie]);
  

  // Intercept back navigation
  useEffect(() => {
    const handlePopState = (e) => {
      // If we're already navigating back (confirmed), allow it
      if (isNavigatingBackRef.current) {
        return;
      }

      // Prevent default back navigation
      e.preventDefault();
      window.history.pushState(null, '', window.location.href);
      
      // Show confirmation popup
      setShowCancelConfirm(true);
    };

    // Push a state to enable back button interception
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle cancel transaction confirmation (for SnacksPage, navigate to seat selection)
  const handleCancelTransaction = useCallback(async () => {
    setIsCancellingTransaction(true);
    
    try {
      // Get Vista transaction ID and cinema ID for API calls
      const locationTransactionId = location.state?.vistaTransactionId;
      const contextTransactionId = bookingTransactionId && typeof bookingTransactionId === 'object' 
        ? bookingTransactionId.transactionId 
        : (bookingTransactionId || null);
      
      const vistaTransactionId = locationTransactionId || contextTransactionId;
      const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.id;
      const currentSessionId = showtime?.id || showtime?.sessionId || showtime?.vistaShowId;
      
      // Cancel Vista transaction to release seats using reinitVistaBooking
      if (vistaTransactionId && vistaCinemaId && currentSessionId) {
        try {
          // console.log('Cancelling Vista transaction to release seats:', {
          //   cinemaId: vistaCinemaId,
          //   sessionId: currentSessionId,
          //   existingTransId: vistaTransactionId,
          // });
          
          // Use reinitVistaBooking which cancels existing transaction and creates new one
          // We'll discard the new transaction and just use the cancellation effect
          const result = await api.reinitVistaBooking(vistaCinemaId, currentSessionId, vistaTransactionId);
          // console.log('Vista transaction cancelled successfully - seats released:', result);
        } catch (cancelError) {
          console.error('Failed to cancel Vista transaction:', cancelError);
          // Continue with navigation even if cancellation fails
          // The transaction might have already expired or been cancelled
        }
      }

      // Clear selected seats and quantity
      setSelectedSeats([]);
      setQuantity(null);
      
      // Clear Vista transaction ID from context
      setVistaTransactionId(null);

      // Close confirmation popup
      setShowCancelConfirm(false);

      // Navigate to seat selection - reinitialization will happen when user selects quantity
      isNavigatingBackRef.current = true;
      navigate('/seat-selection', {
        state: {
          movie,
          showtime,
          theater,
          selectedDate: location.state?.selectedDate,
          showQuantityModal: true, // Flag to show quantity modal immediately
        }
      });
    } catch (error) {
      console.error('Error cancelling transaction:', error);
      showError(`Failed to cancel transaction: ${error.message || 'Please try again.'}`);
    } finally {
      setIsCancellingTransaction(false);
    }
  }, [movie, navigate, location.state?.selectedDate, setQuantity, setSelectedSeats, setVistaTransactionId, theater, bookingTransactionId, showtime]);

  const handleSnacksUpdate = useCallback((updatedSnacks) => {
    setSnacks(updatedSnacks);
    setSelectedSnacks(updatedSnacks);
  }, [setSelectedSnacks]);

  const handleProceedToPayment = useCallback(async () => {
    // Get Vista transaction ID and cinema ID for API calls
    // Priority: location.state (immediate) > context (might have timing issues)
    // Handle null check explicitly (typeof null === 'object' in JavaScript!)
    const locationTransactionId = location.state?.vistaTransactionId;
    const contextTransactionId = bookingTransactionId && typeof bookingTransactionId === 'object' 
      ? bookingTransactionId.transactionId 
      : (bookingTransactionId || null);
    
    // Use location state first (immediate), then fall back to context
    const vistaTransactionId = locationTransactionId || contextTransactionId;
    const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.id;
    
    if (!vistaTransactionId || !vistaCinemaId) {
      console.warn('Missing Vista transaction ID or cinema ID, proceeding without Vista API calls');
      // Still navigate to payment even if Vista IDs are missing
      // But still try to pass what we have
      navigate('/payment', {
        state: {
          movie,
          showtime,
          theater,
          selectedSeats,
          snacks: snacks,
          vistaTransactionId: vistaTransactionId || null, // Pass even if null
          vistaSessionId: showtime?.id || showtime?.vistaShowId || showtime?.sessionId || null,
        }
      });
      return;
    }

    try {
      // Step 1: Add concessions to Vista booking if any snacks are selected
      if (snacks && snacks.length > 0) {
        try {
          // Map snacks to Vista format: { itemId: string, quantity: number }
          // The snack.id or snack.itemId should be the Vista itemId (preserved from API response)
          const vistaItems = snacks.map(snack => ({
            itemId: String(snack.itemId || snack.id), // Prioritize itemId, fallback to id
            quantity: parseInt(snack.quantity || 1, 10),
          }));

          // console.log('Adding concessions to Vista booking:', {
          //   cinemaId: vistaCinemaId,
          //   transId: vistaTransactionId,
          //   items: vistaItems,
          // });

          await api.addConcessionsToVistaBooking(vistaCinemaId, vistaTransactionId, vistaItems);
          
          // console.log('Concessions added to Vista booking successfully');
        } catch (concessionsError) {
          console.error('Error adding concessions to Vista booking:', concessionsError);
          const errorMessage = concessionsError.response?.data?.message || concessionsError.message || 'Failed to add concessions. Please try again.';
          showError(`Error adding concessions: ${errorMessage}`);
          return; // Don't proceed to payment if concessions API fails
        }
      }

      // Step 2: Add booking fee to Vista booking if booking fee is enabled
      const calculatePerSeatBookingFee = () => {
        if (!selectedSeats || selectedSeats.length === 0) {
          return 0;
        }

        // Use tiered booking fee system if tiers are available
        if (bookingFeeTiers && bookingFeeTiers.length > 0) {
          // Calculate total ticket price (we need to get this from seats or showtime)
          // For now, we'll calculate based on seat prices if available
          let totalTicketPrice = 0;
          if (selectedSeats && selectedSeats.length > 0) {
            totalTicketPrice = selectedSeats.reduce((total, seat) => {
              const seatPrice = typeof seat.price === 'number' ? seat.price : parseFloat(seat.price) || 0;
              return total + seatPrice;
            }, 0);
          }

          // Find the appropriate tier for the total ticket price
          const matchingTier = bookingFeeTiers.find(tier => {
            const minPrice = typeof tier.min_price === 'number' ? tier.min_price : parseFloat(tier.min_price) || 0;
            const maxPrice = typeof tier.max_price === 'number' ? tier.max_price : parseFloat(tier.max_price) || 0;
            return totalTicketPrice >= minPrice && totalTicketPrice <= maxPrice;
          });

          if (matchingTier) {
            // Return per-seat booking fee (already includes GST)
            const feePerSeat = typeof matchingTier.booking_fee === 'number' 
              ? matchingTier.booking_fee 
              : parseFloat(matchingTier.booking_fee) || 0;
            return feePerSeat;
          }

          return 0;
        }

        // Fall back to old fixed/percentage system if tiers are not available
        if (!convenienceFeeSettings.enabled) {
          return 0;
        }

        const { type, value } = convenienceFeeSettings;

        if (type === 'fixed') {
          // Fixed amount per seat (already includes GST)
          return value;
        } else if (type === 'percentage') {
          // Percentage of total seat price per seat (already includes GST)
          let totalSeatPrice = 0;
          if (selectedSeats && selectedSeats.length > 0) {
            totalSeatPrice = selectedSeats.reduce((total, seat) => {
              const seatPrice = typeof seat.price === 'number' ? seat.price : parseFloat(seat.price) || 0;
              return total + seatPrice;
            }, 0);
          }
          const totalFee = (totalSeatPrice * value) / 100;
          // Return per-seat fee
          return selectedSeats.length > 0 ? totalFee / selectedSeats.length : 0;
        }

        return 0;
      };

      const perSeatBookingFee = calculatePerSeatBookingFee();
      const seatCount = selectedSeats ? selectedSeats.length : 0;

      if (perSeatBookingFee > 0 && seatCount > 0) {
        try {
          // console.log('Adding booking fee to Vista booking:', {
          //   cinemaId: vistaCinemaId,
          //   transId: vistaTransactionId,
          //   amount: perSeatBookingFee,
          //   quantity: seatCount,
          // });

          await api.addBookingFeeToVistaBooking(vistaCinemaId, vistaTransactionId, perSeatBookingFee, seatCount);
          
          // console.log('Booking fee added to Vista booking successfully');
        } catch (bookingFeeError) {
          console.error('Error adding booking fee to Vista booking:', bookingFeeError);
          const errorMessage = bookingFeeError.response?.data?.message || bookingFeeError.message || 'Failed to add booking fee. Please try again.';
          showError(`Error adding booking fee: ${errorMessage}`);
          return; // Don't proceed to payment if booking fee API fails
        }
      }

      // Step 3: Continue Vista booking (proceed from snacks to payment)
      try {
        // console.log('Continuing Vista booking before payment:', {
        //   cinemaId: vistaCinemaId,
        //   transId: vistaTransactionId,
        // });
        
        await api.continueVistaBooking(vistaCinemaId, vistaTransactionId);
        
        // console.log('Vista booking continued successfully');
      } catch (continueError) {
        console.error('Error continuing Vista booking:', continueError);
        const errorMessage = continueError.response?.data?.message || continueError.message || 'Failed to continue booking. Please try again.';
        showError(`Error: ${errorMessage}`);
        return; // Don't proceed to payment if continue API fails
      }

      // Step 4: Navigate to payment page with all booking data
      navigate('/payment', {
        state: {
          movie,
          showtime,
          theater,
          selectedSeats,
          snacks: snacks, // Pass the actual snacks data
          vistaTransactionId: vistaTransactionId, // Pass Vista transaction ID
          vistaSessionId: showtime?.id || showtime?.vistaShowId || showtime?.sessionId || null, // Pass Vista session ID
        }
      });
    } catch (error) {
      console.error('Unexpected error in handleProceedToPayment:', error);
      showError('An unexpected error occurred. Please try again.');
    }
  }, [navigate, movie, showtime, theater, selectedSeats, snacks, bookingTransactionId, convenienceFeeSettings, bookingFeeTiers]);

  if (!restored) {
    return <div style={{ padding: 40 }}>Restoring booking…</div>;
  }
  
  if (!movie || !showtime || !theater) {
    navigate('/', { replace: true });
    return null;
  }
  

  return (
    <>
      {/* Cancel Transaction Confirmation Popup */}
      <CancelTransactionPopup
        isOpen={showCancelConfirm}
        onConfirm={handleCancelTransaction}
        onCancel={() => setShowCancelConfirm(false)}
        isProcessing={isCancellingTransaction}
        message={{
          title: "Are you sure you want to go back?",
          text: "This will take you back to the seat selection page."
        }}
      />

      {/* Quick Navigation */}
      {/* <div className="quicknav">
        <div className="container">
          <ul>
            <li><a href="#">Group Booking</a></li>
            <li><a href="#">Offers & Promotion</a></li>
            <li><a href="#">FAQ</a></li>
            <li><Link to="/contact-us">Contact Us</Link></li>
          </ul>
        </div>
      </div> */}

      {/* Booking Progress */}
      <div className="booking-progress">
  <div className="container">
    <div className="d-flex justify-content-between align-items-center">
      <button
        className="secondary-btn"
        onClick={() => setShowCancelConfirm(true)}
      >
        <i className="fas fa-arrow-left" style={{ fontSize: '1rem' }}></i>
      </button>
      <ProgressStepper 
        currentStep="snacks"
        movie={movie}
        showtime={showtime}
        theater={theater}
        selectedSeats={selectedSeats}
      />
    </div>
  </div>
</div>


      {/* Snacks Selection Component */}
      <SnacksSelection
        movie={movie}
        showtime={showtime}
        theater={theater}
        selectedSeats={selectedSeats}
        initialSnacks={snacks}
        onSnacksUpdate={handleSnacksUpdate}
        onProceedToPayment={handleProceedToPayment}
      />

{showErrorPopup && (
        <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
          <div className="error-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">
              {errorMessage}
            </div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* FAB Button */}
      {/* <div className="fab-btn">
        <button className="primary-btn help-btn">
          <img src="/assets/img/icons/help.svg" alt="Help" /> Help
        </button>
        <button className="primary-btn fab-mob pulse">
          <i className="fas fa-ticket"></i>
        </button>
      </div> */}
    </>
  );
};

export default SnacksPage;
